/**
 * The 52-card deck, shared by every trick-taking game (Bridge, Whist, Pirate Bridge). Game-specific
 * scoring, such as Bridge's high-card points, stays with the game.
 */

/** A suit, lowest to highest: the order only matters as a tie-break when suggesting trump. */
export type Suit = 'C' | 'D' | 'H' | 'S';
export type Rank = 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15;

export interface Card {
  readonly suit: Suit;
  /** 2..10, then Jack 11, Queen 12, King 13, Ace 14, and 15 for a Joker (see `isJoker`). */
  readonly rank: Rank;
}

export const SUITS: readonly Suit[] = ['C', 'D', 'H', 'S'];
const RANK_CHARS = '23456789TJQKAX';

/** The compact id of a card: rank character then suit, e.g. `AS`, `TD`, `2C`. Stable and JSON-friendly. */
export const cardId = (card: Card): string => `${RANK_CHARS[card.rank - 2]}${card.suit}`;

export function parseCard(id: string): Card {
  const rank = RANK_CHARS.indexOf(id[0] ?? '') + 2;
  const suit = id[1] as Suit;
  if (id.length !== 2 || rank < 2 || !SUITS.includes(suit)) throw new Error(`not a card id: ${id}`);
  return { suit, rank: rank as Rank };
}

/**
 * A Joker is a card of rank 15 whose suit gives its colour: hearts is the red Joker, spades the black one (`XH`, `XS`). It is never in
 * `fullDeck()`, so Bridge, Whist, Hearts and Pirate Bridge can never deal one; a game that wants Jokers (Crazy Eights) adds them itself.
 */
export const JOKER_RANK: Rank = 15;
export const isJoker = (card: Card): boolean => card.rank === JOKER_RANK;
export type JokerColour = 'red' | 'black';
export const jokerCard = (colour: JokerColour): Card => ({ suit: colour === 'red' ? 'H' : 'S', rank: JOKER_RANK });
export const jokerColour = (card: Card): JokerColour => (card.suit === 'H' || card.suit === 'D' ? 'red' : 'black');

export const sameCard = (a: Card, b: Card): boolean => a.suit === b.suit && a.rank === b.rank;

/** Lowest rank of the "Big Cards" short deck: 8 to Ace, 28 cards (see docs/proposals/whist-and-pirate-bridge.md). */
export const BIG_CARDS_MIN_RANK: Rank = 8;

/**
 * The deck sorted by suit then rank: all 52 cards, or with `minRank` only that rank and above
 * (`BIG_CARDS_MIN_RANK` gives the 28-card short deck). The default is the full deck, so every
 * existing caller is unchanged.
 */
export function fullDeck(minRank: Rank = 2): Card[] {
  const out: Card[] = [];
  for (const suit of SUITS) for (let rank = minRank; rank <= 14; rank++) out.push({ suit, rank: rank as Rank });
  return out;
}

export function suitCounts(cards: readonly Card[]): Record<Suit, number> {
  const counts: Record<Suit, number> = { C: 0, D: 0, H: 0, S: 0 };
  for (const c of cards) counts[c.suit] += 1;
  return counts;
}

/** Display order for a hand: spades, hearts, diamonds, clubs; high cards first within a suit. */
export function sortHand(cards: readonly Card[]): Card[] {
  const order: Record<Suit, number> = { S: 0, H: 1, D: 2, C: 3 };
  // A Joker belongs to no suit, so it goes last, red before black.
  return [...cards].sort((a, b) => Number(isJoker(a)) - Number(isJoker(b)) || order[a.suit] - order[b.suit] || b.rank - a.rank);
}
